import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Color, Size, condensedOrgChart, dropOnNode, t, type OrgBranch, type OrgChartNode } from '@bms2/shared';
import { Button } from '../../components/controls';
import { Icon } from '../../components/Icon';
import styles from './OrgTree.module.css';

/**
 * The org chart drawn as a hierarchy, not as an indented list (decision 0463).
 * The root sits at the top, every person who manages somebody takes a column
 * below their own manager, and the elbow between two cards is the reporting
 * line itself.
 *
 * 🔨 A report who manages nobody never takes a column: they stack in one narrow
 * column under their manager, which is what keeps a real organisation on one
 * screen. 🔨 The drawing holds no reporting logic — `condensedOrgChart` shapes
 * it and `dropOnNode` refuses the drop that would loop it, both in
 * `packages/shared`.
 */
export interface OrgTreeProps {
  nodes: readonly OrgChartNode[];
  label: string;
  /** The caption under the name: the title, the department and the report count. */
  renderMeta: (node: OrgChartNode) => ReactNode;
  /** Marks the card carries, such as the department-head badge. */
  renderBadges?: (node: OrgChartNode) => ReactNode;
  /** The card's own control, such as Move person, drawn on hover and on focus. */
  renderActions?: (node: OrgChartNode) => ReactNode;
  /**
   * A card dropped onto another card. 🔨 The pointer drag is never the only
   * path: a host that offers it offers the same move as a card action, which is
   * what a keyboard reaches.
   */
  onDropOn?: (dragged: OrgChartNode, target: OrgChartNode) => void;
}

export function OrgTree({ nodes, label, renderMeta, renderBadges, renderActions, onDropOn }: OrgTreeProps) {
  const [folded, setFolded] = useState<ReadonlySet<string>>(() => new Set());
  const [over, setOver] = useState<string | null>(null);
  const carried = useRef<string | null>(null);
  const branches = useMemo(() => condensedOrgChart(nodes, folded), [nodes, folded]);
  /** Every card that has anybody under it, which is every card the fold control reaches. */
  const managers = useMemo(() => {
    const named = new Set(nodes.map((node) => node.parentId).filter((id): id is string => id !== null && id !== undefined));
    return nodes.filter((node) => named.has(node.id)).map((node) => node.id);
  }, [nodes]);
  const allFolded = managers.length > 0 && managers.every((id) => folded.has(id));

  const fold = (id: string) => setFolded((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  const drop = (targetId: string) => {
    const from = carried.current;
    carried.current = null;
    setOver(null);
    if (!from || !onDropOn) return;
    const move = dropOnNode(nodes, from, targetId);
    if (!move) return;
    const person = nodes.find((node) => node.id === move.id);
    const target = nodes.find((node) => node.id === move.parentId);
    if (person && target) onDropOn(person, target);
  };

  const card = (node: OrgChartNode, below: number, isFolded: boolean) => (
    <div
      className={styles.card}
      data-org-node={node.employmentId}
      data-org-folded={below > 0 ? (isFolded ? 'true' : 'false') : undefined}
      data-drop-target={onDropOn && over === node.id ? 'true' : undefined}
      draggable={onDropOn ? true : undefined}
      onDragStart={onDropOn ? () => { carried.current = node.id; } : undefined}
      onDragOver={onDropOn ? (event) => { event.preventDefault(); setOver(node.id); } : undefined}
      onDragLeave={onDropOn ? () => setOver((current) => (current === node.id ? null : current)) : undefined}
      onDrop={onDropOn ? (event) => { event.preventDefault(); drop(node.id); } : undefined}
    >
      <span className={styles.head}>
        <span className={styles.name}>{node.label}</span>
        {below > 0 ? (
          <button
            type="button"
            className={styles.fold}
            data-org-fold={node.employmentId}
            aria-expanded={!isFolded}
            aria-label={isFolded ? `${t('tree.expand')} · ${below}` : t('tree.collapse')}
            onClick={() => fold(node.id)}
          >
            <Icon meaning={isFolded ? 'expand' : 'collapse'} size="inline" />
            {isFolded ? <span aria-hidden="true">{below}</span> : null}
          </button>
        ) : null}
      </span>
      <span className={styles.meta} data-org-node-meta={node.employmentId}>{renderMeta(node)}</span>
      {renderBadges ? <span className={styles.badges}>{renderBadges(node)}</span> : null}
      {renderActions ? <span className={styles.actions}>{renderActions(node)}</span> : null}
    </div>
  );

  const subtree = (branch: OrgBranch): ReactNode => (
    <>
      {card(branch.node, branch.below, branch.folded)}
      {branch.leaves.length > 0 || branch.branches.length > 0 ? (
        <ul className={styles.group}>
          {branch.leaves.length > 0 ? (
            <li className={styles.slot} data-org-stack={branch.node.employmentId}>
              <ul className={styles.stack}>
                {branch.leaves.map((leaf) => (
                  <li key={leaf.id}>{card(leaf, 0, false)}</li>
                ))}
              </ul>
            </li>
          ) : null}
          {branch.branches.map((child) => (
            <li key={child.node.id} className={styles.slot}>{subtree(child)}</li>
          ))}
        </ul>
      ) : null}
    </>
  );

  return (
    <div className={styles.chart} data-org-tree="true">
      {managers.length > 0 ? (
        <span className={styles.toolbar}>
          <Button
            size={Size.Small}
            color={Color.Inactive}
            text={[allFolded ? t('org.expandAll') : t('org.collapseAll')]}
            actions={[() => setFolded(allFolded ? new Set() : new Set(managers))]}
          />
        </span>
      ) : null}
      <div className={styles.canvas}>
        <ul className={styles.rootGroup} aria-label={label}>
          {branches.map((branch) => (
            <li key={branch.node.id} className={styles.slot}>{subtree(branch)}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
